// src/features/inventory/pages/EditSupplierPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSuppliers } from '../hooks/useSuppliers';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormRow, FormActions } from '../../../components/common/form';

const EMPTY_ADDRESS = {
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'Denmark',
};

export default function EditSupplierPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();

  const {
    currentSupplier,
    loading,
    error,
    clearError,
    fetchSupplierById,
    updateSupplier,
  } = useSuppliers();

  const [form, setForm] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: { ...EMPTY_ADDRESS },
    active: true,
  });
  const [errors, setErrors] = useState({});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    if (supplierId && !currentSupplier) {
      fetchSupplierById(supplierId);
    }
  }, [supplierId]);

  useEffect(() => {
    if (currentSupplier && !initialDataLoaded) {
      setForm({
        name: currentSupplier.name ?? '',
        contact_email: currentSupplier.contact_email ?? '',
        contact_phone: currentSupplier.contact_phone ?? '',
        address: currentSupplier.address ? {
          address_line1: currentSupplier.address.address_line1 ?? '',
          address_line2: currentSupplier.address.address_line2 ?? '',
          postal_code: currentSupplier.address.postal_code ?? '',
          city: currentSupplier.address.city ?? '',
          country: currentSupplier.address.country ?? 'Denmark',
        } : { ...EMPTY_ADDRESS },
        active: currentSupplier.active ?? true,
      });
      setInitialDataLoaded(true);
    }
  }, [currentSupplier, initialDataLoaded]);

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAddressChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const newErrors = {};
    // Country is pre-filled, so it alone doesn't mean an address was entered
    const hasAddressData = Object.entries(form.address).some(([key, v]) => key !== 'country' && v?.trim());
    if (hasAddressData) {
      if (!form.address.address_line1.trim()) newErrors.address_line1 = t('inventory:suppliers.validation.address_line1_required');
      if (!form.address.postal_code.trim()) newErrors.postal_code = t('inventory:suppliers.validation.postal_code_required');
      if (!form.address.city.trim()) newErrors.city = t('inventory:suppliers.validation.city_required');
      if (!form.address.country.trim()) newErrors.country = t('inventory:suppliers.validation.country_required');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const addressPayload = hasAddressData ? {
      address_line1: form.address.address_line1.trim(),
      address_line2: form.address.address_line2.trim() || null,
      postal_code: form.address.postal_code.trim(),
      city: form.address.city.trim(),
      country: form.address.country.trim(),
    } : null;

    const result = await updateSupplier(supplierId, {
      name: form.name.trim(),
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      address: addressPayload,
      active: form.active,
    });
    if (result.success) {
      showSuccess(t('inventory:suppliers.success.updated'));
      navigateBack('..', { replace: true });
    } else {
      showError(result.error);
    }
  }, [updateSupplier, supplierId, form, showSuccess, showError, navigateBack, t]);

  const handleCancel = useCallback(() => {
    navigateBack('..', { replace: true });
  }, [navigateBack]);

  if (loading && !currentSupplier) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentSupplier && !loading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={() => navigateBack('..', { relative: 'path' })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t('inventory:suppliers.edit_form.title')}</Typography>
        </Box>
        <Alert severity="error">{t('inventory:suppliers.not_found')}</Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('inventory:suppliers.edit_form.title')}
      subtitle={currentSupplier ? t('inventory:suppliers.edit_form.subtitle', { name: currentSupplier.name }) : undefined}
      crumb={currentSupplier?.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={errors}
      error={error}
      onErrorClose={clearError}
    >
      {initialDataLoaded && (
        <>
          <FormSection
            description={currentSupplier && (
              <>{t('inventory:suppliers.grid.code')}: <strong>{currentSupplier.code}</strong></>
            )}
          >
            <TextField
              label={t('inventory:suppliers.create_form.name')}
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <FormRow>
              <TextField
                label={t('inventory:suppliers.create_form.email')}
                value={form.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                fullWidth
                type="email"
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
              <TextField
                label={t('inventory:suppliers.create_form.phone')}
                value={form.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 50 } }}
              />
            </FormRow>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => handleChange('active', e.target.checked)}
                />
              }
              label={t('inventory:suppliers.edit_form.active_label')}
            />
          </FormSection>

          <FormSection title={t('inventory:suppliers.create_form.address_section')}>
            <TextField
              label={t('inventory:suppliers.create_form.address_line1')}
              value={form.address.address_line1}
              onChange={(e) => handleAddressChange('address_line1', e.target.value)}
              fullWidth
              error={Boolean(errors.address_line1)}
              helperText={errors.address_line1}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <TextField
              label={t('inventory:suppliers.create_form.address_line2')}
              value={form.address.address_line2}
              onChange={(e) => handleAddressChange('address_line2', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <FormRow widths={[1, 3]} minWidth={140}>
              <TextField
                label={t('inventory:suppliers.create_form.postal_code')}
                value={form.address.postal_code}
                onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                fullWidth
                error={Boolean(errors.postal_code)}
                helperText={errors.postal_code}
                slotProps={{ htmlInput: { maxLength: 20 } }}
              />
              <TextField
                label={t('inventory:suppliers.create_form.city')}
                value={form.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                fullWidth
                error={Boolean(errors.city)}
                helperText={errors.city}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
            </FormRow>
            <TextField
              label={t('inventory:suppliers.create_form.country')}
              value={form.address.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              fullWidth
              error={Boolean(errors.country)}
              helperText={errors.country}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </FormSection>

          <FormActions
            onCancel={handleCancel}
            submitType="submit"
            submitIcon={<SaveIcon />}
            loading={loading}
          />
        </>
      )}
    </FormPage>
  );
}
